// assets/gwp-tote.js
(function () {
  const TOTE_VARIANT_ID = 51280360243482; // THE KNOT TOTE
  const THRESHOLD_CENTS = 4500;
  let busy = false;

  const log = (...a) => {
    if (location.search.includes('gwpdebug')) console.log('[GWP]', ...a);
  };

  // Run ensure once, then repeatedly for a short window to survive async drawer renders
  function burstEnsure(ms = 2200, every = 180) {
    const start = Date.now();
    const tick = async () => {
      await ensureGWPInternal();
      if (Date.now() - start < ms) setTimeout(tick, every);
    };
    tick();
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    });
    return r.json();
  }

  async function addVariant(id, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [{ id, quantity: qty }] })
    });
    if (!res.ok)
      throw new Error(
        (await res.json().catch(() => ({})))?.description || 'Add failed'
      );
    return res.json();
  }

  // Variant-based updates so we don't rely on fragile line keys
  async function updateByVariant(updatesObj) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updatesObj })
    });
    return res.json();
  }

  const eligibleSubtotal = cart =>
    (cart.items || []).reduce((sum, l) => {
      return l.id === TOTE_VARIANT_ID
        ? sum
        : sum + (l.final_line_price ?? l.line_price ?? 0);
    }, 0);

  async function ensureGWPInternal() {
    if (busy) return;
    busy = true;
    try {
      const cart = await fetchCart();
      const subtotal = eligibleSubtotal(cart);
      const meets = subtotal >= THRESHOLD_CENTS;
      const tote = (cart.items || []).find(i => i.id === TOTE_VARIANT_ID);
      log(
        'subtotal excl tote',
        subtotal,
        'meets?',
        meets,
        'tote?',
        !!tote,
        'qty',
        tote?.quantity
      );

      // If eligible & no tote -> add
      if (meets && !tote) {
        try {
          await addVariant(TOTE_VARIANT_ID, 1);
          log('added tote');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch (e) {
          log('add failed', e.message);
        }
        busy = false;
        return;
      }

      // If not eligible & tote present -> remove by variant
      if (!meets && tote) {
        await updateByVariant({ [TOTE_VARIANT_ID]: 0 });
        log('removed tote via /cart/update.js');

        // Force the page to reload so the drawer/cart UI immediately reflects the new cart
        window.location.reload();

        // We won't reach here because of reload, but keep it tidy:
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        busy = false;
        return;
      }

      // Clamp tote to 1 if somehow >1
      if (meets && tote?.quantity > 1) {
        await updateByVariant({ [TOTE_VARIANT_ID]: 1 });
        log('clamped tote qty to 1 via /cart/update.js');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) {
      log('ensure error', e);
    } finally {
      busy = false;
    }
  }

  // ——— Detect any cart change and fire a burst of ensures ———
  function onPossibleCartChange() {
    burstEnsure();
  }

  // 1) Listen to common custom events (Pipeline + others)
  [
    'cart:updated',
    'cart:change',
    'cart:refresh',
    'ajaxProduct:added',
    'product:added',
    'variant:changed',
    'shopify:section:load',
    'shopify:section:select',
    'shopify:section:deselect',
    'drawer:opened',
    'drawer:closed',
    'cart-drawer:opened',
    'cart-drawer:updated'
  ].forEach(evt => document.addEventListener(evt, onPossibleCartChange));

  // 2) Inputs/buttons inside the cart drawer
  document.addEventListener(
    'click',
    e => {
      const inDrawer = e.target.closest(
        '[data-section-id][id*="cart"], [data-cart-drawer], [data-section-type="cart-drawer"]'
      );
      const btn = e.target.closest(
        '[href*="cart/change"], [data-cart-remove], .cart__remove, .qty__button, [data-qty-plus], [data-qty-minus], form[action*="/cart/add"], button[name="add"], button.add-to-cart'
      );
      if (inDrawer && btn) onPossibleCartChange();
    },
    true
  );

  document.addEventListener(
    'change',
    e => {
      const inDrawer = e.target.closest(
        '[data-section-id][id*="cart"], [data-cart-drawer], [data-section-type="cart-drawer"]'
      );
      const el = e.target;
      if (
        inDrawer &&
        (el?.name === 'updates[]' ||
          el?.dataset?.quantityInput !== undefined ||
          el?.classList?.contains('js-qty__num'))
      ) {
        onPossibleCartChange();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    e => {
      const f = e.target;
      if (f?.action?.includes('/cart/add') || f?.id?.includes('cart'))
        onPossibleCartChange();
    },
    true
  );

  // 3) Monkey-patch fetch & XHR to react to ANY /cart* request
  try {
    const origFetch = window.fetch;
    window.fetch = async function (...args) {
      const res = await origFetch.apply(this, args);
      try {
        const req = args[0];
        const url = typeof req === 'string' ? req : req?.url || '';
        if (/\/cart(\/|\.js|\?|$)/.test(url)) onPossibleCartChange();
      } catch {}
      return res;
    };
  } catch {}
  try {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.addEventListener('loadend', function () {
        try {
          if (
            typeof url === 'string' &&
            /\/cart(\/|\.js|\?|$)/.test(url)
          )
            onPossibleCartChange();
        } catch {}
      });
      return origOpen.call(this, method, url, ...rest);
    };
  } catch {}

  // 4) Observe DOM churn throughout the drawer
  new MutationObserver(() => onPossibleCartChange()).observe(
    document.documentElement,
    { childList: true, subtree: true }
  );

  // 5) Initial + safety passes
  window.addEventListener('load', () => burstEnsure());
  window.addEventListener('pageshow', () => burstEnsure());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') burstEnsure(1400, 160);
  });
})();